import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateCopyTradeSubscriptionPayload,
  UpdateCopyTradeSubscriptionPayload,
} from "@crypto-trading/common/browser";
import {
  listCopyTradeLeaders,
  getCopyTradeLeaderDetail,
  getCopyTradeLeaderPositions,
  listCopyTradeSubscriptions,
  listCopyTradeMirroredPositions,
  subscribeToCopyTradeLeader,
  unsubscribeFromCopyTrade,
  updateCopyTradeSubscription,
} from "@/api/copy-trade";

export const copyTradeKeys = {
  all: ["copy-trade"] as const,
  leaders: (source: string) => [...copyTradeKeys.all, "leaders", source] as const,
  leaderDetail: (portfolioId: string, source: string) =>
    [...copyTradeKeys.all, "leader-detail", portfolioId, source] as const,
  leaderPositions: (portfolioId: string, source: string) =>
    [...copyTradeKeys.all, "leader-positions", portfolioId, source] as const,
  subscriptions: (userId: string) =>
    [...copyTradeKeys.all, "subscriptions", userId] as const,
  mirroredPositions: (subscriptionId: string, userId: string) =>
    [
      ...copyTradeKeys.all,
      "mirrored-positions",
      subscriptionId,
      userId,
    ] as const,
};

export const useCopyTradeLeaders = (source = "binance") => {
  return useQuery({
    queryKey: copyTradeKeys.leaders(source),
    queryFn: () => listCopyTradeLeaders(source),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCopyTradeLeaderDetail = (
  portfolioId: string | undefined,
  source = "binance",
  enabled = true
) => {
  return useQuery({
    queryKey: copyTradeKeys.leaderDetail(portfolioId || "", source),
    queryFn: () => getCopyTradeLeaderDetail(portfolioId!, source),
    enabled: enabled && !!portfolioId,
    staleTime: 30 * 60 * 1000,
  });
};

export const useCopyTradeLeaderPositions = (
  portfolioId: string | undefined,
  source = "binance",
  enabled = true
) => {
  return useQuery({
    queryKey: copyTradeKeys.leaderPositions(portfolioId || "", source),
    queryFn: () => getCopyTradeLeaderPositions(portfolioId!, source),
    enabled: enabled && !!portfolioId,
    staleTime: 4 * 60 * 1000,
  });
};

export const useCopyTradeSubscriptions = (userId: string | undefined) => {
  return useQuery({
    queryKey: copyTradeKeys.subscriptions(userId || "unknown"),
    queryFn: () => listCopyTradeSubscriptions(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
};

export const useCopyTradeMirroredPositions = (
  subscriptionId: string | undefined,
  userId: string | undefined,
  enabled = true
) => {
  return useQuery({
    queryKey: copyTradeKeys.mirroredPositions(
      subscriptionId || "",
      userId || ""
    ),
    queryFn: () =>
      listCopyTradeMirroredPositions(subscriptionId!, userId!),
    enabled: enabled && !!subscriptionId && !!userId,
    staleTime: 30 * 1000,
  });
};

export const useSubscribeCopyTrade = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: CreateCopyTradeSubscriptionPayload;
    }) => subscribeToCopyTradeLeader(userId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: copyTradeKeys.subscriptions(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: [...copyTradeKeys.all, "mirrored-positions"],
      });
    },
  });
};

export const useUpdateCopyTradeSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      userId,
      payload,
    }: {
      subscriptionId: string;
      userId: string;
      payload: UpdateCopyTradeSubscriptionPayload;
    }) => updateCopyTradeSubscription(subscriptionId, userId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: copyTradeKeys.subscriptions(variables.userId),
      });
    },
  });
};

export const useUnsubscribeCopyTrade = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      userId,
    }: {
      subscriptionId: string;
      userId: string;
    }) => unsubscribeFromCopyTrade(subscriptionId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: copyTradeKeys.subscriptions(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: copyTradeKeys.mirroredPositions(
          variables.subscriptionId,
          variables.userId
        ),
      });
    },
  });
};
